import React, {useEffect, useState, useMemo} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import CryptoJS from "crypto-js";
import * as yup from "yup";
import {ApiDetails, KEY, loginState} from "../../dummyData";

// Material-UI Components
import {
    Snackbar,
    Stack,
    Alert,
    Backdrop,
    CircularProgress,
    useMediaQuery,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Chip,
    Box,
    Typography,
    Paper,
    Fade
} from "@mui/material";
import {
    CalendarMonth as CalendarIcon,
    Business as BusinessIcon,
    KeyboardArrowDown as ArrowDownIcon
} from "@mui/icons-material";

// Custom Components
import FeaturedInfo from "../../components/featuredInfo/FeaturedInfo";
import WidgetLg from "../../components/widgetLG/WidgetLg";
import ProgressBars from "../../components/ProgressBars";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import {LogoutOutlined} from "@mui/icons-material";
import DialogContentText from "@mui/material/DialogContentText";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {TextField} from "@material-ui/core";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import {TransitionProps} from "@mui/material/transitions";
import Slide from "@mui/material/Slide";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import OnboardingStepOne from "../../components/customDialogs/onboardingDialog";

// CSS
import "./home.css";

const Transition = React.forwardRef(function Transition(props: TransitionProps & {
    children: React.ReactElement<any, any>,
}, ref: React.Ref<unknown>) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function for safe decryption
const safeDecrypt = (key) => {
    try {
        const encrypted = localStorage.getItem(key);
        if (!encrypted) return null;
        const decrypted = CryptoJS.AES.decrypt(encrypted, KEY).toString(CryptoJS.enc.Utf8);
        if (!decrypted) return null;
        JSON.parse(decrypted); // Validate JSON
        return decrypted;
    } catch (error) {
        console.error(`Failed to decrypt ${key}:`, error);
        return null;
    }
};

const apiFetch = async (url, options) => {
    try {
        const response = await fetch(url, options);
        if (!response.ok) throw new Error("API request failed");
        return await response.json();
    } catch (error) {
        console.error(error);
        return null;
    }
};

// Generate year options (current year plus 10 years back and 2 years forward)
const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 10; year <= currentYear + 2; year++) {
        years.push(year);
    }
    return years;
};

const HomeV2 = ({refreshSideBar, topBar}) => {
    const matches = useMediaQuery("(max-width:700px)");
    const isMobile = useMediaQuery("(max-width:768px)");
    const location = useLocation();
    const navigate = useNavigate();

    // New state for year and month selection
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [isDataLoading, setIsDataLoading] = useState(false);

    const [modals, setModals] = useState({
        subscriptionModal: false,
        paymentModal: false,
        snackbarOpen: false,
    });

    const [subscription, setSubscription] = useState(safeDecrypt("subscriptionDetails"));
    let [originalText, setOriginalText] = useState(safeDecrypt("myMap"));
    const [originalText2, setOriginalText2] = useState(safeDecrypt("portfolioDetails"));

    const [set, updateSet] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState();
    const [loading, setLoading] = useState(false);

    const totalUnits = useMemo(() => {
        try {
            if (!originalText2) return 0;
            return new Map(JSON.parse(originalText2)).get("number_of_units") || 0;
        } catch {
            return 0;
        }
    }, [originalText2]);

    const handleSnackbarClose = () => {
        setModals((prev) => ({...prev, snackbarOpen: false}));
    };

    const specificOptions = new Set(['option1', 'option2', 'option3']);

    const [subscriptionModal, setSubscriptionModal] = useState(false);
    const [paymentModal, setPaymentModal] = useState(false);
    const [dateSubscriptionExpired, setDateSubscriptionExpired] = useState(null);
    const [, setDateSubscribed] = useState(null);
    const [, setEmailAddress] = useState(null);
    const [amount, setAmount] = useState({amount: 0});
    const [subscriptionAmount, setSubscriptionAmount] = useState(0);
    const [subscribeButtonDisabled, setSubscribeButtonDisabled] = useState(true);
    const [status, setStatus] = useState(localStorage.getItem("isNew"));

    const getUserDetails = async (userDetails, month = null, year = null) => {
        setIsDataLoading(true);

        // Add month and year to the request if provided
        const requestBody = {
            ...userDetails,
            ...(month && {month}),
            ...(year && {year})
        };

        const response = await apiFetch(ApiDetails + "pegasus/visionary/dashboard/get/details", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify(requestBody),
        });

        if (response) {
            const map1 = new Map(Object.entries(response));
            const details = new Map(Object.entries(userDetails));
            const subscription = new Map();

            subscription.set("subscription_expired", details.get("subscription_expired"));
            subscription.set("subscription_amount", details.get("subscription_amount"));

            const myMap = CryptoJS.AES.encrypt(
                JSON.stringify(Array.from(details.entries())),
                KEY
            ).toString();
            const portfolioDetails = CryptoJS.AES.encrypt(
                JSON.stringify(Array.from(map1.entries())),
                KEY
            ).toString();
            const subscriptionDetails = CryptoJS.AES.encrypt(
                JSON.stringify(Array.from(subscription.entries())),
                KEY
            ).toString();

            localStorage.setItem("myMap", myMap);
            localStorage.setItem("isNew", userDetails.new);
            localStorage.portfolioDetails = portfolioDetails;
            localStorage.subscriptionDetails = subscriptionDetails;

            setOriginalText(myMap);
            setOriginalText2(portfolioDetails);
            setSubscription(subscriptionDetails);

            const periodText = month && year ? `${getMonthName(month)} ${year}` : 'current period';

            showSnackbar({
                title: "Success!",
                message: `Dashboard details updated for ${periodText}`,
                type: "success",
                position: {vertical: "bottom", horizontal: "right"},
            });
            refreshSideBar();
            topBar();
        } else {
            showSnackbar({
                title: "Error!",
                message: "Failed to update dashboard details",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
        setIsDataLoading(false);
    };

    // Helper function to get month name
    const getMonthName = (monthNumber) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[monthNumber - 1];
    };

    // Handle year change
    const handleYearChange = (event) => {
        const newYear = event.target.value;
        setSelectedYear(newYear);

        // Get current user details and fetch data for new year
        const userDetails = (() => {
            const decrypted = safeDecrypt("myMap");
            if (decrypted) {
                try {
                    return Object.fromEntries(JSON.parse(decrypted));
                } catch {
                    return null;
                }
            }
            return null;
        })();

        if (userDetails) {
            getUserDetails(userDetails, selectedMonth, newYear);
        }
    };

    // Handle month change
    const handleMonthChange = (event) => {
        const newMonth = event.target.value;
        setSelectedMonth(newMonth);

        // Get current user details and fetch data for new month
        const userDetails = (() => {
            const decrypted = safeDecrypt("myMap");
            if (decrypted) {
                try {
                    return Object.fromEntries(JSON.parse(decrypted));
                } catch {
                    return null;
                }
            }
            return null;
        })();

        if (userDetails) {
            getUserDetails(userDetails, newMonth, selectedYear);
        }
    };

    useEffect(() => {
        const userDetails = location.state?.userDetails || (() => {
            const decrypted = safeDecrypt("myMap");
            if (decrypted) {
                try {
                    return Object.fromEntries(JSON.parse(decrypted));
                } catch {
                    return null;
                }
            }
            return null;
        })();

        if (userDetails) {
            getUserDetails(userDetails, selectedMonth, selectedYear);
        } else {
            navigate("/");
            showSnackbar({
                title: "Error!",
                message: "No user details found. Please log in.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }

        let userDetailsArray = [];
        try {
            if (originalText) {
                userDetailsArray = JSON.parse(originalText);
            }
        } catch (error) {
            console.error("Failed to parse originalText:", error);
        }

        let date_subscribed, email_address;
        if (Array.isArray(userDetailsArray) && userDetailsArray.length > 0) {
            userDetailsArray.forEach(([key, value]) => {
                if (key === "date_subscription_renewed") {
                    date_subscribed = new Date(value);
                }
                if (key === "email_address") {
                    setEmailAddress(value);
                }
            });
        }

        setDateSubscribed(date_subscribed);

        if (status !== "yes") {
            try {
                const subscriptionArray = subscription ? JSON.parse(subscription) : [];
                const subscriptionExpired = subscriptionArray[0]?.[1] || false;

                if (date_subscribed) {
                    date_subscribed.setMonth(date_subscribed.getMonth() + 1);
                    setDateSubscriptionExpired(
                        date_subscribed.getDate() + " " +
                        date_subscribed.toLocaleString("en-US", {month: "long"}) + ", " +
                        date_subscribed.getFullYear()
                    );
                } else {
                    setDateSubscriptionExpired("First subscription");
                }

                if (subscriptionExpired && status !== "yes") {
                    const amount = Math.ceil(subscriptionArray[1]?.[1] || 0);
                    setSubscriptionAmount(amount);
                    setSubscribeButtonDisabled(false);
                    setSubscriptionModal(true);
                }
            } catch (e) {
                console.error("Subscription parsing error:", e);
            }
        }

        updateSet(true);
    }, []);

    let initialPaymentValues = {
        phone_number: (() => {
            try {
                return originalText && JSON.parse(originalText)?.[3]?.[1] || "";
            } catch {
                return "";
            }
        })(),
    };
    let basicSchema = yup.object().shape({
        phone_number: yup.string().phone("KE", true).required("Required"),
        amount: yup.number().min(1, "Amount must be more than 1 KSH"),
    });

    const [open, setOpen] = useState(loginState);

    const handleClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
        setPaymentModal(false);
    };

    const [tenantLoading, setTenantLoading] = useState(false);

    const processPayment = async (values) => {
        if (values.phone_number.toString().startsWith("0")) {
            values.phone_number = "254" + values.phone_number.toString().substring(1);
        } else if (values.phone_number.toString().startsWith("1") || values.phone_number.toString().startsWith("7")) {
            values.phone_number = "254" + values.phone_number.toString();
        }

        let landlord_id = "";
        try {

            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);

            if (originalText) {
                landlord_id = JSON.parse(originalText)?.[0]?.[1] || "";
            }
        } catch {
            console.error("originalText: " + originalText);
            console.error("Failed to parse landlord_id from originalText");
        }

        let body = {
            BusinessShortCode: "6122464",
            Password: "",
            Timestamp: "",
            TransactionType: "CustomerBuyGoodsOnline",
            Amount: amount,
            PartyA: values.phone_number,
            PartyB: "5726811",
            PhoneNumber: values.phone_number,
            CallBackURL: "https://api.pegasus-pms.co.ke/api/pegasus/visionary/payments/subscription",
            AccountReference: "ITORAMA SOLUTIONS",
            TransactionDesc: "Subscription payment",
            landlord_id,
        };

        const response = await apiFetch(ApiDetails + "pegasus/visionary/mpesa/landlord/StkPush", {
            method: "POST",
            body: JSON.stringify(body),
        });

        if (response) {
            setTenantLoading(false);
            showSnackbar({
                title: "Success!",
                message: "Kindly wait for M-Pesa 💵 prompt on your phone",
                type: "success",
                position: {vertical: "bottom", horizontal: "right"},
            });
        } else {
            setTenantLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Payment failed",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
        handleClose();
    };

    const logout = () => {
        window.location.href = "/";
        localStorage.clear();
    };

    let map = new Map();
    try {
        if (originalText) {
            map = new Map(JSON.parse(originalText));
        }
    } catch (e) {
        console.error("Failed to create map from originalText:", e);
    }

    const handleClick = (message) => {
        setMessage(message.text);
        setSeverity(message.type);
        setOpen(true);
    };

    const viewTopBarClick = () => {
        topBar();
    };

    const yearOptions = generateYearOptions();
    const monthOptions = [
        {value: 1, label: 'January'},
        {value: 2, label: 'February'},
        {value: 3, label: 'March'},
        {value: 4, label: 'April'},
        {value: 5, label: 'May'},
        {value: 6, label: 'June'},
        {value: 7, label: 'July'},
        {value: 8, label: 'August'},
        {value: 9, label: 'September'},
        {value: 10, label: 'October'},
        {value: 11, label: 'November'},
        {value: 12, label: 'December'},
    ];

    return (
        <>
            <Backdrop open={loading || isDataLoading} sx={{zIndex: (theme) => theme.zIndex.drawer + 1}}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            <Dialog
                open={subscriptionModal}
                TransitionComponent={Transition}
                keepMounted
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle sx={{color: "#398DD2", fontWeight: "bold", textAlign: "center"}}>
                    Renew Subscription
                </DialogTitle>
                <DialogContent sx={{px: 3, py: 2, textAlign: "center"}}>
                    <Box sx={{width: "100%", pb: 2}}>
                        <Box
                            sx={{
                                p: 2,
                                color: "#959EAD",
                                borderRadius: 2,
                                border: "2px solid #398DD2",
                                backgroundColor: "#F8FAFD",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <strong>Price:</strong> {subscriptionAmount.toLocaleString("en-US", {
                            style: "currency",
                            currency: "KSH",
                        })}
                            <Button
                                sx={{color: "green", borderColor: "green"}}
                                disabled={subscribeButtonDisabled}
                                onClick={() => {
                                    setAmount(parseInt(subscriptionAmount));
                                    setPaymentModal(true);
                                }}
                                variant="outlined"
                            >
                                Subscribe Now
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{color: "red", fontWeight: "bold", pt: 2}}>Important:</Box>
                    <Box sx={{color: "#000", fontSize: "1rem", mt: 1}}>
                        Your subscription expired on: <strong>{dateSubscriptionExpired}</strong>
                    </Box>
                    <Box sx={{color: "#555", fontSize: "0.95rem", mt: 1}}>
                        Renew your subscription to avoid interruptions.
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: matches ? "column" : "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pt: 3,
                        }}
                    >
                        <Box sx={{color: "#6C757D", fontSize: "0.85rem", textAlign: "left"}}>
                            After subscribing, you'll receive a confirmation message from IT-orama. Kindly log in again
                            to update your payment details.
                        </Box>
                        <Button
                            disabled={subscribeButtonDisabled}
                            onClick={logout}
                            variant="outlined"
                            sx={{
                                textTransform: "uppercase",
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                borderColor: "#d32f2f",
                                color: "#d32f2f",
                                '&:hover': {borderColor: "#b71c1c", backgroundColor: "#ffebee"},
                            }}
                        >
                            <LogoutOutlined sx={{pr: 0.5}}/> Logout
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog
                open={paymentModal}
                TransitionComponent={Transition}
                keepMounted
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle style={{color: "#398DD2"}}>Make Payment</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        <Formik
                            initialValues={initialPaymentValues}
                            validationSchema={basicSchema}
                            onSubmit={processPayment}
                        >
                            {(props) => (
                                <Form noValidate>
                                    <p>Enter your phone number to receive a payment prompt.</p>
                                    <Field
                                        as={TextField}
                                        name="phone_number"
                                        label="Phone Number"
                                        placeholder="e.g., 07XXXXXXXX"
                                        error={props.errors.phone_number && props.touched.phone_number}
                                        helperText={<ErrorMessage className="error" name="phone_number"/>}
                                        required
                                        variant="outlined"
                                        style={{marginTop: "10px", width: "100%"}}
                                    />
                                    <Field
                                        as={TextField}
                                        name="amount"
                                        label="Amount"
                                        type="number"
                                        required
                                        variant="outlined"
                                        value={amount}
                                        InputLabelProps={{shrink: true}}
                                        style={{marginTop: "10px", width: "100%"}}
                                    />

                                    <div
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            marginTop: "20px",
                                        }}
                                    >
                                        <Button onClick={handleClose} style={{color: "red"}}>
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            type="submit"
                                            size="small"
                                            loading={tenantLoading}
                                            startIcon={<SendIcon/>}
                                            style={{color: "green"}}
                                            loadingPosition="start"
                                        >
                                            Submit
                                        </LoadingButton>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </DialogContentText>
                </DialogContent>
            </Dialog>

            {set && (
                status === "yes" ? (
                    <div className="home">
                        <Stack spacing={2} sx={{width: "100%"}}>
                            <Snackbar open={open} autoHideDuration={2000} onClose={handleClose}>
                                <Alert onClose={handleClose} severity={severity} sx={{width: "100%"}}>
                                    {message}
                                </Alert>
                            </Snackbar>
                        </Stack>
                        <OnboardingStepOne details={map} snackBar={handleClick} topBar={viewTopBarClick}/>
                    </div>
                ) : (
                    <div className={subscriptionModal ? "home blur" : "home"}>
                        {/* Header Section with Property Label and Period Selector */}
                        <Fade in={true} timeout={800}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    backgroundColor: '#fafbfc',
                                    borderRadius: 3,
                                    border: '1px solid #e8eaed',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1.5,
                                    }}
                                >
                                    {/* Left side - Property Label */}
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        <Chip
                                            icon={<BusinessIcon sx={{color: "#398DD2 !important", fontSize: 16}}/>}
                                            label="For All Properties"
                                            size="small"
                                            sx={{
                                                backgroundColor: "#f0f8ff",
                                                color: "#398DD2",
                                                fontWeight: 500,
                                                fontSize: isMobile ? "0.75rem" : "0.8rem",
                                                height: 28,
                                                '& .MuiChip-icon': {
                                                    color: "#398DD2",
                                                },
                                                border: "1px solid #398DD240",
                                                borderRadius: 2,
                                            }}
                                        />

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "#999",
                                                fontStyle: "italic",
                                                fontSize: "0.7rem",
                                            }}
                                        >
                                            Showing data for{" "}
                                            <span style={{color: "#398DD2", fontWeight: 500}}>
                                            {getMonthName(selectedMonth)} {selectedYear}
                                        </span>
                                        </Typography>
                                    </Box>

                                    {/* Right side - Period Selectors */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            backgroundColor: 'white',
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0',
                                            px: 1.5,
                                            py: 0.5,
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        }}
                                    >
                                        {/* Period Label */}
                                        {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <CalendarIcon sx={{ color: "#666", fontSize: 16 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#666",
                                                    fontWeight: 500,
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Period:
                                            </Typography>
                                        </Box>

                                         Month Selector
                                        <FormControl
                                            variant="standard"
                                            size="small"
                                            sx={{
                                                minWidth: isMobile ? 80 : 90,
                                                '& .MuiInput-underline:before': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:after': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                                    borderBottom: 'none',
                                                },
                                            }}
                                        >
                                            <Select
                                                value={selectedMonth}
                                                onChange={handleMonthChange}
                                                disabled={isDataLoading}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: '#398DD2',
                                                    '& .MuiSelect-select': {
                                                        padding: '2px 4px',
                                                        '&:focus': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        color: '#398DD2',
                                                        fontSize: 16,
                                                    },
                                                }}
                                            >
                                                {monthOptions.map((month) => (
                                                    <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.8rem' }}>
                                                        {isMobile ? month.label.slice(0, 3) : month.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Typography sx={{ color: "#ddd", fontSize: "0.9rem", mx: 0.5 }}>|</Typography>

                                         Year Selector
                                        <FormControl
                                            variant="standard"
                                            size="small"
                                            sx={{
                                                minWidth: isMobile ? 60 : 70,
                                                '& .MuiInput-underline:before': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:after': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                                    borderBottom: 'none',
                                                },
                                            }}
                                        >
                                            <Select
                                                value={selectedYear}
                                                onChange={handleYearChange}
                                                disabled={isDataLoading}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: '#398DD2',
                                                    '& .MuiSelect-select': {
                                                        padding: '2px 4px',
                                                        '&:focus': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        color: '#398DD2',
                                                        fontSize: 16,
                                                    },
                                                }}
                                            >
                                                {yearOptions.map((year) => (
                                                    <MenuItem key={year} value={year} sx={{ fontSize: '0.8rem' }}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>*/}
                                    </Box>
                                </Box>

                                {/* Selected Period Display */}
                                {/*<Box sx={{ mt: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#999",
                                            fontStyle: "italic",
                                            fontSize: "0.7rem",
                                        }}
                                    >
                                        Showing data for{" "}
                                        <span style={{ color: "#398DD2", fontWeight: 500 }}>
                                            {getMonthName(selectedMonth)} {selectedYear}
                                        </span>
                                    </Typography>
                                </Box>*/}
                            </Paper>
                        </Fade>

                        {/* Main Dashboard Content */}
                        <Fade in={!isDataLoading} timeout={600}>
                            <div>
                                <FeaturedInfo/>
                                <ProgressBars/>
                                <WidgetLg/>
                            </div>
                        </Fade>
                    </div>
                )
            )}
        </>
    );
};

export default HomeV2;